import React from "react";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import Text from "@ui/Text";
import t from "../../../../../../../i18n";
import ButtonIconV2 from "@ui/ButtonIconV2";
import {
  ArrowLeftLine,
  Chat4Line,
  HistoryLine,
  More2Fill,
  PencilLine,
  ToolsFill,
} from "@ui/IconV2/react";
import { color } from "@ui";
import Button from "@ui/Button";
import { useCanUserDoOnProject } from "../../../../../../canUserDo";
import Menu from "@ui/Menu";
import PriorityFill from "@ui/IconV2/react/PriorityFill";
import type { FaultEventStatus, PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import { getEventPriorityText } from "../../../../utils/event";
import { matchRouterPath, toPath } from "../../../../../../../router/helpers/path";
import { toBasenamePath } from "../../../../../../../router/helpers/navigation";
import { useLocation, useNavigate } from "react-router";
import type { Reparability } from "../../../../../../../api/Reparability";
import { useTranslations } from "../../../../../../../api/useTranslations";
import { Tag } from "@ui/Tag";
import { BadgeEventStatus } from "../../../Events/BadgeEventStatus";
import { EventName } from "../../../../components/EventName";

interface P {
  projectId: string;
  name: string;
  hasMultiFault: boolean;
  status: FaultEventStatus;
  priority: PriorityFaultEvent;
  reparability: Reparability | null;
  action: () => void;
  optionsMenu?: React.ReactNode;
  optionBadges?: React.ReactNode[];
  tag?: React.ReactNode;
  onClickComment: () => void;
  onClickActivity: () => void;
  isCommentActive: boolean;
  isActivityActive: boolean;
}

const TitleHeader = ({
  projectId,
  name,
  hasMultiFault,
  status,
  priority,
  reparability,
  tag,
  action,
  optionsMenu,
  optionBadges = [],
  onClickComment,
  onClickActivity,
  isCommentActive,
  isActivityActive,
}: P) => {
  const canUserReadProject = useCanUserDoOnProject("PROJECT_READ", projectId);
  const translations = useTranslations();
  const canUserEditFaultEvent = useCanUserDoOnProject("PROJECT_WRITE", projectId);
  const navigate = useNavigate();
  const location = useLocation();

  const goto = React.useCallback(
    (e: React.MouseEvent) => {
      let targetPath = toPath("/projects/:projectId/fault-events", {
        projectId,
      });
      const previousLocationWasClosedFaultEventsPage =
        location.state === null
          ? false
          : matchRouterPath("/projects/:projectId/fault-events/closed", location.state.from);
      if (previousLocationWasClosedFaultEventsPage) {
        targetPath = toPath("/projects/:projectId/fault-events/closed", {
          projectId,
        });
      }

      if (e.metaKey || e.ctrlKey) {
        window.open(toBasenamePath(targetPath));
      } else {
        void navigate(targetPath);
      }
    },
    [projectId, location, navigate],
  );

  return (
    <>
      <div className={title}>
        <div className={titleHeader}>
          <ButtonIconV2
            data-cy="go-back-events-page"
            className={gridItemRedirection}
            onClick={goto}
          >
            <ArrowLeftLine size="m" color={color.carbon800} />
          </ButtonIconV2>
          <div className={titleHeaderDescription}>
            <Text type="h4" className={nameDescription}>
              <EventName name={name} hasMultiFault={hasMultiFault} />
            </Text>
            <div className={separator} />
            <span className={priorityDescription}>
              <PriorityFill priority={priority} />
              <Text as="span">{getEventPriorityText(priority)}</Text>
            </span>
            <div className={separator} />
            <BadgeEventStatus status={status} onClick={() => null} />
            {reparability !== null && (
              <>
                <div className={separator} />
                <Tag>
                  <ToolsFill color={color.carbon800} size="s" />
                  <Text as="span">{t("reparability", reparability, translations)}</Text>
                </Tag>
              </>
            )}
            {optionBadges.map(badge => (
              <>
                <div className={separator} />
                {badge}
              </>
            ))}
            {tag}
            {canUserEditFaultEvent && (
              <Button
                variant="ghost"
                leftIcon={<PencilLine size="s" color={color.titanium800} />}
                onClick={action}
                slim
              >
                {t("project.fault_event.header.button.edit")}
              </Button>
            )}
          </div>
          <div className={titleHeader_action}>
            {canUserReadProject && (
              <>
                <ButtonIconV2
                  data-cy="event-comments-button"
                  title={t("project.event.header.button.comments.tooltip")}
                  aria-label={t("project.event.header.button.comments.tooltip")}
                  onClick={onClickComment}
                  checked={isCommentActive}
                >
                  <Chat4Line size="m" />
                </ButtonIconV2>
                <ButtonIconV2
                  data-cy="event-activity-button"
                  title={t("project.event.header.button.activity.tooltip")}
                  aria-label={t("project.event.header.button.activity.tooltip")}
                  onClick={onClickActivity}
                  checked={isActivityActive}
                >
                  <HistoryLine size="m" />
                </ButtonIconV2>
              </>
            )}
            {canUserEditFaultEvent && (
              <>
                {optionsMenu && (
                  <Menu overlay={optionsMenu}>
                    <ButtonIconV2 data-cy="edit-event-name" onClick={() => null}>
                      <More2Fill size="m" color={color.carbon800} />
                    </ButtonIconV2>
                  </Menu>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TitleHeader;

// #region CSS

const title = css`
  z-index: 3;
  width: 100%;
  padding: ${spacings.s};
  background-color: ${colors.hydrogen};
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${colors.titanium200};
`;

const titleHeader = css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacings.xs};
  /* overflow: hidden; */
`;
const titleHeaderDescription = css`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
  flex-wrap: wrap;
  /* overflow: hidden; */
`;
const separator = css`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin: 0 6px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background-color: ${color.carbon800};
`;
const nameDescription = css`
  display: block;
  align-items: center;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  min-width: 25px;
`;
const priorityDescription = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  color: ${color.carbon800};
`;
const titleHeader_action = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacings.xs};

  & > * {
    flex-grow: 0;
  }
`;

const gridItemRedirection = css`
  grid-column: 1 / 2;
  grid-row: 1 / 2;
  margin-right: ${spacings.xxs};
`;
